import { FC, useState, MouseEvent, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import ConfirmationDialog from 'src/components/DialogBox/ConfirmationDialog/ConfirmationDialog';
import RejectDialog from 'src/components/DialogBox/RejectDialog/RejectDialog';
import ViewOrderPaymentDialog from 'src/components/DialogBox/OrderPaymentDialog/ViewOrderPaymentDialog';
import AddOrderPaymentDialog from 'src/components/DialogBox/OrderPaymentDialog/AddOrderPaymentDialog';
import {
  apiUrls,
  successMessages,
  orderPaymentDialogTitles,
  orderPaymentDialogSubtitles,
  DATE_FORMATS,
} from 'src/utils/properties.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import {
  OrderPayment,
  OrderPaymentStatus,
  OrderPaymentActionMenuProps,
  OrderPaymentPayload,
} from 'src/models/OrderPayment.interface';
import { PaymentMode } from 'src/models/enums/PaymentMode.enum';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { StyledDatePickerErrorText } from 'src/components/StyledComponents/StyledUnitPicker';
import ErrorIcon from 'src/components/InputFields/ErrorIcon';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { orderPaymentInitialValues } from 'src/utils/initialValues.utils';
import { orderPaymentDynamicValidation } from '../../utils/validationScheme.utils';
import TextField from 'src/components/InputFields/TextField';
import TextArea from 'src/components/InputFields/TextArea';
import PaymentModePicker from 'src/components/InputFields/PaymentModePicker';
import CreatedDatePicker from 'src/components/InputFields/DatePicker';
import {
  ActionMenuButton,
  ActionMenuPopover,
  MenuItemsWrapper,
  MenuItemWithIcon,
} from 'src/components/StyledComponents/StyledActionMenu';
import FormikSelectField from 'src/components/InputFields/FormikSelectField';
import {
  formatNumberWithCommas,
  handleNumberFieldChange,
} from 'src/utils/format.utils';

const OrderPaymentActionMenu: FC<OrderPaymentActionMenuProps> = ({
  id,
  orderId,
  status,
  banks,
  fetchPayments,
  deletePayment,
  onBanksFetch,
}) => {
  const { showSnackbar } = StyledSnackbar();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewData, setViewData] = useState<OrderPayment | null>(null);
  const [loading, setLoading] = useState(false);
  const [remainingAmount, setRemainingAmount] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const isPendingApproval =
    status === OrderPaymentStatus.PENDING_APPROVAL ||
    status === 'Pending Approval';

  const canView = true;
  const canEdit = isPendingApproval;
  const canApproveReject = isPendingApproval;
  const canDelete = isPendingApproval;

  const accountOptions = banks.map((bank) => ({
    label: `${bank.bankName} - ${bank.accountNumber}`,
    value: bank.id.toString(),
  }));

  const handleClose = () => setAnchorEl(null);

  const handleClick = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const formik = useFormik({
    initialValues: orderPaymentInitialValues,
    validationSchema: orderPaymentDynamicValidation,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: () => {
      // Form submission handled by individual action handlers
    },
  });

  useEffect(() => {
    const mode = formik.values.paymentMode;
    if (
      (mode === PaymentMode.CHEQUE || mode === PaymentMode.ONLINE) &&
      banks.length === 0 &&
      onBanksFetch
    ) {
      onBanksFetch();
    }
  }, [formik.values.paymentMode, banks.length, onBanksFetch]);

  const handleViewDialogOpen = async () => {
    handleClose();
    try {
      const response = await axiosTokenInstance.get(
        `${apiUrls.GET_ORDER_PAYMENT_BY_ID}/${id}`
      );
      const paymentData = response.data.data || response.data;

      setViewData({
        id: paymentData.id,
        orderId: paymentData.orderId,
        paymentDate: paymentData.paymentDate,
        paidAmount: paymentData.paidAmount,
        paymentMode: paymentData.paymentMode,
        receivedBy: paymentData.receiverName,
        status: paymentData.status,
        approvedBy: paymentData.approvedBy,
        approvedDate: paymentData.approvedDate,
        notes: paymentData.notes,
        rejectionNotes: paymentData.rejectionNotes,
        chequeNumber: paymentData.chequeNumber,
        chequeDate: paymentData.chequeDate,
        chequeStatus: paymentData.chequeStatus,
        chequeRealizedDate: paymentData.chequeRealizedDate,
        bankName: paymentData.bankName,
        accountNumber: paymentData.accountNumber,
        transactionReference: paymentData.paymentRefNo,
        createdAt: paymentData.createdAt,
        updatedAt: paymentData.updatedAt,
      });
      setViewDialogOpen(true);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleViewDialogClose = () => {
    setViewDialogOpen(false);
    setViewData(null);
  };

  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
    handleClose();
  };

  const handleDeleteConfirm = async () => {
    try {
      if (id) {
        await deletePayment(id);
      }
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleApproveDialogOpen = () => {
    setApproveDialogOpen(true);
    handleClose();
  };

  const handleApproveConfirm = async () => {
    try {
      const response = await axiosTokenInstance.patch(
        `${apiUrls.TOGGLE_ORDER_PAYMENT_STATUS}/${id}`,
        {
          id: id,
          status: 'Approved',
        }
      );
      showSnackbar(
        response.data.message || successMessages.PAYMENT_APPROVED_SUCCESSFULLY,
        'success'
      );
      fetchPayments();
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setApproveDialogOpen(false);
    }
  };

  const handleRejectDialogOpen = () => {
    setRejectDialogOpen(true);
    handleClose();
  };

  const handleRejectConfirm = async (reason: string) => {
    try {
      const response = await axiosTokenInstance.patch(
        `${apiUrls.TOGGLE_ORDER_PAYMENT_STATUS}/${id}`,
        {
          id: id,
          status: 'Rejected',
          rejectionNotes: reason,
        }
      );
      showSnackbar(
        response.data.message || successMessages.PAYMENT_REJECTED_SUCCESSFULLY,
        'success'
      );
      fetchPayments();
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setRejectDialogOpen(false);
    }
  };

  const handleEditDialogOpen = async () => {
    handleClose();
    try {
      const [paymentResponse, orderResponse] = await Promise.all([
        axiosTokenInstance.get(`${apiUrls.GET_ORDER_PAYMENT_BY_ID}/${id}`),
        axiosTokenInstance.get(`${apiUrls.GET_ORDER_BY_ID}/${orderId}`),
      ]);
      const paymentData = paymentResponse.data.data || paymentResponse.data;
      const orderDetails = orderResponse.data;

      const grandTotal = Number(orderDetails?.grandTotal) || 0;
      const totalPaidAmount = Number(orderDetails?.totalPaidAmount) || 0;
      const currentPaymentAmount = Number(paymentData.paidAmount) || 0;

      setRemainingAmount(
        Math.max(grandTotal - totalPaidAmount + currentPaymentAmount, 0)
      );

      formik.setValues({
        id: paymentData.id,
        orderId: paymentData.orderId,
        paymentDate:
          paymentData.paymentDate?.split('T')[0] ||
          new Date().toISOString().split('T')[0],
        amount: paymentData.paidAmount?.toString() || '',
        paymentMode: paymentData.paymentMode,
        status: paymentData.status,
        chequeNumber: paymentData.chequeNumber || '',
        chequeDate:
          paymentData.chequeDate?.split('T')[0] ||
          new Date().toISOString().split('T')[0],
        chequeStatus: paymentData.chequeStatus || '',
        bankAccountId:
          paymentData.bankAccountId?.toString() ||
          paymentData.bankAccount?.id?.toString() ||
          '',
        transactionReference: paymentData.paymentRefNo || '',
        notes: paymentData.notes || '',
      });

      // Fetch banks if needed based on payment mode
      if (
        (paymentData.paymentMode === PaymentMode.CHEQUE ||
          paymentData.paymentMode === PaymentMode.ONLINE) &&
        banks.length === 0 &&
        onBanksFetch
      ) {
        onBanksFetch();
      }

      setEditDialogOpen(true);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    formik.resetForm();
  };

  const handleEditSubmit = async () => {
    // Define all fields that should be touched
    const touchedFields: Record<string, boolean> = {
      paymentDate: true,
      amount: true,
      paymentMode: true,
    };

    // Add conditional fields based on payment mode
    if (formik.values.paymentMode === PaymentMode.CHEQUE) {
      touchedFields.chequeNumber = true;
      touchedFields.chequeDate = true;
      touchedFields.bankAccountId = true;
    } else if (formik.values.paymentMode === PaymentMode.ONLINE) {
      touchedFields.bankAccountId = true;
      touchedFields.transactionReference = true;
    }

    // Validate and get errors using the dynamic validation schema
    try {
      await orderPaymentDynamicValidation.validate(formik.values, {
        abortEarly: false,
      });
    } catch (validationError) {
      const yupError = validationError as {
        inner: Array<{ path: string; message: string }>;
      };
      const errors = yupError.inner.reduce(
        (
          acc: Record<string, string>,
          error: { path: string; message: string }
        ) => {
          acc[error.path] = error.message;
          return acc;
        },
        {}
      );
      // Set touched first, then errors
      await formik.setTouched(touchedFields, false);
      formik.setErrors(errors);
      return;
    }

    const enteredAmount = parseFloat(
      formik.values.amount.toString().replace(/,/g, '')
    );
    if (remainingAmount > 0 && enteredAmount > remainingAmount) {
      await formik.setTouched({ ...touchedFields }, false);
      formik.setErrors({
        amount: `Amount exceeds payable amount`,
      });
      return;
    }

    setLoading(true);

    try {
      const cleanAmount = formik.values.amount.toString().replace(/,/g, '');
      const payload: OrderPaymentPayload = {
        id: id,
        paymentDate: dayjs(formik.values.paymentDate).format(DATE_FORMATS.API),
        amount: parseFloat(cleanAmount),
        paymentMode: formik.values.paymentMode,
      };

      if (formik.values.notes) {
        payload.notes = formik.values.notes;
      }

      if (formik.values.paymentMode === PaymentMode.CHEQUE) {
        payload.chequeNumber = formik.values.chequeNumber;
        payload.chequeDate = dayjs(formik.values.chequeDate).format(
          DATE_FORMATS.API
        );
        payload.bankAccountId = formik.values.bankAccountId;
      } else if (formik.values.paymentMode === PaymentMode.ONLINE) {
        payload.bankAccountId = formik.values.bankAccountId;
        payload.paymentRefNo = formik.values.transactionReference;
      }

      const response = await axiosTokenInstance.put(
        `${apiUrls.EDIT_ORDER_PAYMENT}/payments`,
        payload
      );
      showSnackbar(
        response.data.message || successMessages.PAYMENT_UPDATED_SUCCESSFULLY,
        'success'
      );
      fetchPayments();
      handleEditDialogClose();
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ActionMenuButton
        onClick={handleClick}
        component="i"
        className="bx bx-dots-horizontal-rounded"
      />
      <ActionMenuPopover open={open} anchorEl={anchorEl} onClose={handleClose}>
        <MenuItemsWrapper>
          {canView && (
            <MenuItemWithIcon
              onClick={handleViewDialogOpen}
              iconClassName="bx bx-file-find"
              text="View"
            />
          )}
          {canEdit && (
            <MenuItemWithIcon
              onClick={handleEditDialogOpen}
              iconClassName="bx bx-edit"
              text="Edit"
            />
          )}
          {canApproveReject && (
            <>
              <MenuItemWithIcon
                onClick={handleApproveDialogOpen}
                iconClassName="bx bx-check"
                text="Approve"
              />
              <MenuItemWithIcon
                onClick={handleRejectDialogOpen}
                iconClassName="bx bx-x"
                text="Reject"
              />
            </>
          )}
          {canDelete && (
            <MenuItemWithIcon
              onClick={handleDeleteDialogOpen}
              iconClassName="bx bx-trash"
              text="Delete"
            />
          )}
        </MenuItemsWrapper>
      </ActionMenuPopover>
      <ViewOrderPaymentDialog
        open={viewDialogOpen}
        onClose={handleViewDialogClose}
        payment={viewData}
      />
      <ConfirmationDialog
        height="275px"
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={orderPaymentDialogTitles.DELETE}
        subtitle={orderPaymentDialogSubtitles.DELETE}
        confirmText="Delete"
        confirmButtonColor="var(--error)"
        alertIconColor="var(--error)"
      />
      <ConfirmationDialog
        height="275px"
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        onConfirm={handleApproveConfirm}
        title={orderPaymentDialogTitles.APPROVE}
        subtitle={orderPaymentDialogSubtitles.APPROVE}
        confirmText="Approve"
        confirmButtonColor="var(--success)"
        alertIconColor="var(--success)"
      />
      <RejectDialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={handleRejectConfirm}
        title="Reject Payment"
        subtitle="Rejection Note"
        subtitleSecondary=""
        placeholder="Reason for rejection"
        confirmText="Reject"
      />
      <AddOrderPaymentDialog
        open={editDialogOpen}
        handleClose={handleEditDialogClose}
        title={orderPaymentDialogTitles.EDIT}
        subtitle={orderPaymentDialogSubtitles.EDIT}
        onSubmit={formik.handleSubmit}
        buttonDisabled={loading}
        buttonLabel="Update"
        onSaveClick={handleEditSubmit}
        paymentModeSection={
          <>
            <Box mb={0}>
              <PaymentModePicker
                color="var(--subtext)"
                width="48.5%"
                id="paymentMode"
                label="Payment Method"
                formikProps={formik}
                customMessage="Select payment method"
                showLabel={false}
              />
            </Box>
            {formik.values.paymentMode === PaymentMode.CHEQUE ? (
              <>
                <Box
                  display={'flex'}
                  justifyContent={'space-between'}
                  gap={'16px'}
                  mb={0}
                  mt={-3}
                >
                  <Box flex={1}>
                    <TextField
                      color="var(--subtext)"
                      width="100%"
                      id="chequeNumber"
                      label="Cheque Number"
                      formikProps={formik}
                    />
                  </Box>
                  <Box flex={1}>
                    <Box mb={3}>
                      <Typography
                        fontFamily="Outfit"
                        fontWeight="400"
                        fontSize="14px"
                        color="var(--subtext)"
                        mb={2}
                      >
                        Cheque Date
                      </Typography>
                      <CreatedDatePicker
                        fontSize="14px"
                        height="40px"
                        color="var(--subtext)"
                        width="100%"
                        name="chequeDate"
                        placeholder="Select date"
                        formikProps={formik}
                        showLabel={false}
                      />
                      {formik.touched.chequeDate &&
                        formik.errors.chequeDate && (
                          <StyledDatePickerErrorText>
                            <ErrorIcon />
                            {formik.errors.chequeDate}
                          </StyledDatePickerErrorText>
                        )}
                    </Box>
                  </Box>
                </Box>
                <Box
                  display={'flex'}
                  justifyContent={'space-between'}
                  mb={0}
                  gap={2}
                >
                  <Box flex={1}>
                    <FormikSelectField
                      id="bankAccountId"
                      label="Bank Account"
                      formikProps={formik}
                      width="48.5%"
                      options={accountOptions}
                    />
                  </Box>
                </Box>
              </>
            ) : formik.values.paymentMode === PaymentMode.ONLINE ? (
              <Box
                display={'flex'}
                justifyContent={'space-between'}
                mb={0}
                mt={-3}
              >
                <Box flex={1}>
                  <FormikSelectField
                    id="bankAccountId"
                    label="Bank Account"
                    formikProps={formik}
                    width="100%"
                    options={accountOptions}
                  />
                </Box>
                <Box flex={1} display={'flex'} justifyContent={'flex-end'}>
                  <TextField
                    color="var(--subtext)"
                    width="100%"
                    id="transactionReference"
                    label="Payment Reference"
                    formikProps={formik}
                  />
                </Box>
              </Box>
            ) : null}
          </>
        }
      >
        <Box
          display={'flex'}
          justifyContent={'space-between'}
          mb={0}
          gap={'16px'}
        >
          <Box flex={1}>
            <TextField
              color="var(--subtext)"
              width="100%"
              id="amount"
              label="Amount"
              formikProps={formik}
              type="text"
              value={formatNumberWithCommas(formik.values.amount)}
              onChange={(e) => handleNumberFieldChange(e, formik, 'amount')}
              placeholder={
                remainingAmount > 0
                  ? `Remaining: ${remainingAmount.toLocaleString()}`
                  : ''
              }
            />
          </Box>
          <Box
            flex={1}
            display={'flex'}
            flexDirection={'column'}
            justifyContent={'flex-end'}
          >
            <CreatedDatePicker
              mb={3}
              fontSize="14px"
              label="Payment Date"
              showLabel={true}
              color="var(--subtext)"
              width="100%"
              height="40px"
              name="paymentDate"
              placeholder="Payment Date"
              formikProps={formik}
            />
            {formik.touched.paymentDate && formik.errors.paymentDate && (
              <StyledDatePickerErrorText top="2px">
                <ErrorIcon />
                {formik.errors.paymentDate}
              </StyledDatePickerErrorText>
            )}
          </Box>
        </Box>
        <Box mb={0}>
          <TextArea
            id="notes"
            label="Notes (Optional)"
            formikProps={formik}
            placeholder="Add any notes about this payment..."
            height="80px"
            width="100%"
            showLabel={true}
            rows={2}
            overflowY="hidden"
          />
        </Box>
      </AddOrderPaymentDialog>
    </>
  );
};

export default OrderPaymentActionMenu;
